/**
 * Maintenance Mode Page
 * Displays when the system is under maintenance
 */

import React from 'react';
import { Wrench, Clock, Shield } from 'lucide-react';
import { commonStyles } from '../../../styles/common';

interface MaintenancePageProps {
  message?: string;
}

export const MaintenancePage: React.FC<MaintenancePageProps> = ({
  message = "LucidDock is currently under maintenance. We'll be back shortly."
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/20 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Main Card */}
        <div className={`bg-white ${commonStyles.radius['3xl']} ${commonStyles.shadows.card} p-8 md:p-12 border border-gray-200`}>
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* Animated background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full blur-xl opacity-20 animate-pulse" />

              {/* Icon container */}
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <Wrench className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className={`${commonStyles.font['2xl']} ${commonStyles.weight.bold} text-center text-gray-900 mb-4`}>
            Under Maintenance
          </h1>

          {/* Message */}
          <p className={`${commonStyles.font.lg} text-center text-gray-600 mb-8 leading-relaxed`}>
            {message}
          </p>

          {/* Status indicators */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className={`flex items-center gap-3 p-4 ${commonStyles.radius.xl} bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200`}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className={`${commonStyles.font.sm} ${commonStyles.weight.semibold} text-gray-900`}>
                  Temporary Downtime
                </p>
                <p className={`${commonStyles.font.xs} text-gray-600`}>
                  We'll be back soon
                </p>
              </div>
            </div>

            <div className={`flex items-center gap-3 p-4 ${commonStyles.radius.xl} bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200`}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className={`${commonStyles.font.sm} ${commonStyles.weight.semibold} text-gray-900`}>
                  Your Data is Safe
                </p>
                <p className={`${commonStyles.font.xs} text-gray-600`}>
                  All data is secure
                </p>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <div className={`text-center p-4 ${commonStyles.radius.xl} bg-gray-50 border border-gray-200`}>
            <p className={`${commonStyles.font.sm} text-gray-600`}>
              Thank you for your patience. If you have urgent questions, please contact{' '}
              <a
                href="mailto:support@luciddock.com"
                className="text-indigo-600 hover:underline font-medium"
              >
                support@luciddock.com
              </a>
            </p>
          </div>
        </div>

        {/* Logo/Branding */}
        <div className="text-center mt-8">
          <p className={`${commonStyles.font.sm} text-gray-500`}>
            LucidDock
          </p>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
